import { Box, Icon, IconButton, Table as RT, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { flexRender, Header, Table as ReactTable } from '@tanstack/react-table';
import { BiMinus, BiPlus } from 'react-icons/bi';

import { TableFilterStatus } from '@/cip/widgets/Table/TableFilterStatus';
import { TableHeaderCellContent } from '@/cip/widgets/Table/TableHeaderCellContent';

import { LifecycleRow } from './buildPositionLifeCycleColumns';

const STICKY_HEADER_Z = 2;
const STICKY_COL_Z = 1;

type OutlineCell = {
    header: Header<LifecycleRow, unknown>;
    colSpan: number;
    childIds?: string[];
};

// Groups each delta column together with its currently-visible detail children, so the outline
// row above the header can render a single spanning +/- control per group, Excel-style.
const buildOutlineCells = (
    headers: Header<LifecycleRow, unknown>[],
    detailChildIdsByColumnId: Record<string, string[]>,
): OutlineCell[] => {
    const cells: OutlineCell[] = [];

    for (let i = 0; i < headers.length; ) {
        const header = headers[i];
        const childIds = detailChildIdsByColumnId[header.column.id];

        if (childIds?.length) {
            let colSpan = 1;
            while (i + colSpan < headers.length && childIds.includes(headers[i + colSpan].column.id)) {
                colSpan += 1;
            }
            cells.push({ header, colSpan, childIds });
            i += colSpan;
        } else {
            cells.push({ header, colSpan: 1 });
            i += 1;
        }
    }

    return cells;
};

// Kept local rather than folded into the shared cip Table component, since sticky
// header/summary-row aren't expected to be reused elsewhere.
export const PositionLifeCycleGrid = ({
    table,
    accentColorByColumnId,
    detailChildIdsByColumnId,
}: {
    table: ReactTable<LifecycleRow>;
    accentColorByColumnId: Record<string, string>;
    detailChildIdsByColumnId: Record<string, string[]>;
}) => {
    return (
        <Box w="100%">
            <TableFilterStatus table={table} />
            <Box overflow="auto" maxHeight="70vh" pb={2}>
                {/* border-collapse lets adjacent sticky rows share a border pixel, through which the
                    row painted later in DOM order can bleed - separate avoids that seam entirely. */}
                <RT size="sm" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <Thead position="sticky" top={0} zIndex={STICKY_HEADER_Z} background="gray.800">
                        <Tr>
                            {buildOutlineCells(table.getHeaderGroups()[0]?.headers ?? [], detailChildIdsByColumnId).map(
                                ({ header, colSpan, childIds }) => {
                                    const isStickyCol = Boolean(header.column.columnDef.meta?.isSticky);
                                    const isExpanded = childIds?.some((id) => table.getColumn(id)?.getIsVisible());

                                    // The button always sits over the delta column itself (the first slot of the
                                    // span); only the connector line grows to bracket the expanded detail columns.
                                    const hasVisibleChildren = colSpan > 1;

                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={colSpan}
                                            borderColor="gray.600"
                                            paddingY={0}
                                            paddingX={1}
                                            height="22px"
                                            position={isStickyCol ? 'sticky' : undefined}
                                            left={isStickyCol ? 0 : undefined}
                                            zIndex={isStickyCol ? STICKY_COL_Z : undefined}
                                            background="gray.800"
                                        >
                                            {childIds && (
                                                <Box position="relative" height="22px">
                                                    <IconButton
                                                        aria-label={
                                                            isExpanded
                                                                ? 'collapse detail columns'
                                                                : 'expand detail columns'
                                                        }
                                                        icon={<Icon as={isExpanded ? BiMinus : BiPlus} boxSize={3} />}
                                                        variant="outline"
                                                        borderColor="whiteAlpha.600"
                                                        minW="18px"
                                                        h="18px"
                                                        position="absolute"
                                                        left="2px"
                                                        top="50%"
                                                        transform="translateY(-50%)"
                                                        onClick={() =>
                                                            table.setColumnVisibility((old) => {
                                                                const next = { ...old };
                                                                childIds.forEach((id) => {
                                                                    next[id] = !isExpanded;
                                                                });
                                                                return next;
                                                            })
                                                        }
                                                    />
                                                    {hasVisibleChildren && (
                                                        <Box
                                                            position="absolute"
                                                            top="50%"
                                                            left="24px"
                                                            right="2px"
                                                            borderTopWidth="1px"
                                                            borderColor="whiteAlpha.600"
                                                        >
                                                            <Box
                                                                position="absolute"
                                                                top="0"
                                                                right={0}
                                                                height="6px"
                                                                width="1px"
                                                                background="whiteAlpha.600"
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Th>
                                    );
                                },
                            )}
                        </Tr>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isStickyCol = Boolean(header.column.columnDef.meta?.isSticky);
                                    const background = accentColorByColumnId[header.column.id];

                                    return (
                                        <Th
                                            key={header.id}
                                            whiteSpace="nowrap"
                                            borderColor="gray.600"
                                            paddingY={1}
                                            paddingX={3}
                                            position={isStickyCol ? 'sticky' : undefined}
                                            left={isStickyCol ? 0 : undefined}
                                            zIndex={isStickyCol ? STICKY_COL_Z : undefined}
                                            background={background}
                                        >
                                            <TableHeaderCellContent header={header} table={table} />
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                        {table.getFooterGroups().map((footerGroup) => (
                            <Tr key={footerGroup.id} fontWeight="bold">
                                {footerGroup.headers.map((header) => {
                                    const isStickyCol = Boolean(header.column.columnDef.meta?.isSticky);

                                    return (
                                        <Th
                                            key={header.id}
                                            whiteSpace="nowrap"
                                            borderColor="gray.600"
                                            paddingY={1}
                                            paddingX={3}
                                            textTransform="none"
                                            position={isStickyCol ? 'sticky' : undefined}
                                            left={isStickyCol ? 0 : undefined}
                                            zIndex={isStickyCol ? STICKY_COL_Z : undefined}
                                            background="gray.700"
                                        >
                                            {flexRender(header.column.columnDef.footer, header.getContext())}
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => (
                            <Tr key={row.id} _hover={{ background: 'whiteAlpha.100' }}>
                                {row.getVisibleCells().map((cell) => {
                                    const isStickyCol = Boolean(cell.column.columnDef.meta?.isSticky);

                                    return (
                                        <Td
                                            key={cell.id}
                                            fontSize="sm"
                                            paddingY={1}
                                            paddingX={2}
                                            whiteSpace="nowrap"
                                            borderColor="gray.600"
                                            position={isStickyCol ? 'sticky' : undefined}
                                            left={isStickyCol ? 0 : undefined}
                                            zIndex={isStickyCol ? STICKY_COL_Z : undefined}
                                            background={isStickyCol ? 'gray.700' : undefined}
                                            width={`${cell.column.getSize()}px`}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Tbody>
                </RT>
            </Box>
        </Box>
    );
};
